import type { DocumentReferenceLink, IntakeData } from './types';
import { getSpecialtyTag } from './specialtyTags';
import { buildFinalizeBody, parseDisclosure } from './buildConsents';
import {
  getDialCodeForCountry,
  repairIntakePhoneFields,
} from './phoneNumber';

const getBackendUrl = () =>
  import.meta.env.VITE_JEMA_BACKEND_URL || "http://localhost:3000";

const getApiVersion = () => {
  const configuredVersion = import.meta.env.VITE_JEMA_API_VERSION;
  if (typeof configuredVersion !== "string") {
    return "v2";
  }

  const normalizedVersion = configuredVersion.trim().replace(/^\/+|\/+$/g, "");
  return normalizedVersion.length > 0 ? normalizedVersion : "v2";
};

const getVersionedApiPath = (path: string) =>
  `${getBackendUrl()}/api/${getApiVersion()}${path.startsWith("/") ? path : `/${path}`}`;

/**
 * Short-lived draft token issued by the Manager API on form creation / continuation verify.
 * Every form read/write is supposed to carry it once the server flag enforcing it is on;
 * until then the header is simply omitted when we do not have one.
 * ponytail: module-level only — a full page reload drops it, and the flow that needs it
 * (OTP continuation) re-issues one anyway. Persist it if a reload ever has to keep it.
 */
let draftToken: string | null = null;

export const setDraftToken = (token: string | null | undefined): void => {
  const trimmed = typeof token === "string" ? token.trim() : "";
  draftToken = trimmed.length > 0 ? trimmed : null;
};

export const getDraftToken = (): string | null => draftToken;

/** Merges `Authorization: Bearer <draftToken>` into headers when a token is known. */
const withDraftAuth = (
  headers?: Record<string, string>,
): Record<string, string> =>
  draftToken
    ? { ...(headers ?? {}), Authorization: `Bearer ${draftToken}` }
    : { ...(headers ?? {}) };

const captureDraftToken = (payload: unknown): void => {
  if (!payload || typeof payload !== "object") return;
  const root = payload as Record<string, unknown>;
  const data = (root.data ?? root) as Record<string, unknown>;
  const token = data?.draftToken ?? root.draftToken;
  if (typeof token === "string") setDraftToken(token);
};

const handleApiResponse = async (response: Response) => {
  if (!response.ok) {
    let errorMessage = "An error occurred";
    let errorDetails: unknown = null;
    try {
      const errorData = await response.json();
      errorDetails = errorData;
      
      // Log full error for debugging
      console.error(
        "API Error Response:",
        JSON.stringify(
          {
            status: response.status,
            statusText: response.statusText,
            url: response.url,
            errorData,
          },
          null,
          2
        )
      );
      
      if (errorData.error) {
        if (typeof errorData.error === "string") {
          errorMessage = errorData.error;
        } else if (errorData.error.message) {
          errorMessage = errorData.error.message;
        } else if (errorData.error.code) {
          errorMessage = errorData.error.code;
        } else {
          errorMessage = JSON.stringify(errorData.error);
        }
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else {
        errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      }
    } catch {
      // If JSON parsing fails, try to get text
      try {
        const text = await response.text();
        console.error("API Error (non-JSON):", text);
        errorMessage = text || `HTTP ${response.status}: ${response.statusText}`;
      } catch {
        errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      }
    }
    
    const error = Object.assign(new Error(errorMessage), { details: errorDetails, status: response.status });
    throw error;
  }

  return response.json();
};

export interface IntakeMedia extends Record<string, unknown> {
  id: string;
  originalFilename?: string;
  filename?: string;
  mimeType?: string;
  fileSize?: number;
  downloadUrl?: string;
  mediaType?: string;
  status?: string;
  photoPosition?: string;
  category?: string;
}

type StartIntakeApiResponse = {
  data: {
    formUid: string;
    referenceUid: string;
    specialty: string;
    status: string;
    patientCreated: boolean;
    requiresVerification: boolean;
    patientId?: string;
  };
};

type V2IntakeSubmissionPayload = {
  organizationId?: string | null;
  specialtyId?: string | null;
  channel?: string;
  identity: Record<string, unknown>;
  answers?: Array<Record<string, unknown>>;
  media?: Array<Record<string, unknown>>;
  rawPayload?: Record<string, unknown>;
};

type V2IntakeReviewDecision =
  | "approved"
  | "rejected"
  | "needs_more_information";

type V2IntakeReviewPayload = {
  decision: V2IntakeReviewDecision;
  notes?: string | null;
  organizationId?: string | null;
  priority?: "low" | "medium" | "high" | "urgent";
  specialtyId?: string | null;
};

type MagicLinkChallenge = {
  method: string;
  challengeId?: string;
  expiresAt?: string;
  requiresOtpFallback: boolean;
  alreadySent?: boolean;
  sentAt?: string;
};

type OtpChallenge = {
  challengeId: string;
  method: string;
  expiresAt: string;
  message: string;
};

type FinalizeIntakeApiResponse = {
  data: {
    intakeForm: Record<string, unknown>;
    serviceRequest?: Record<string, unknown>;
    magicLink?: MagicLinkChallenge;
    otpChallenge?: OtpChallenge;
  };
};

export type MediaUploadResponse = {
  data: {
    uploadUrl: string;
    bucketName: string;
    storagePath: string;
    expiresIn: number;
  };
};

export type MediaRegistrationResponse = {
  data?: {
    media?: {
      id: string;
      mediaType: string;
      status?: string;
      downloadUrl?: string;
      downloadUrlExpiresIn?: number;
      photoPosition?: string;
      photoSequence?: number;
    };
  };
};

type BatchPresignUploadRequest = {
  clientReference: string;
  mimeType: string;
  size: number;
  originalFilename?: string;
};

type BatchPresignUploadResponse = {
  data: {
    uploads: Array<{
      clientReference: string;
      uploadUrl: string;
      bucketName: string;
      storagePath: string;
      expiresIn: number;
    }>;
  };
};

type RegisteredMedia = NonNullable<MediaRegistrationResponse["data"]>["media"];

type BatchMediaRegistrationResponse = {
  data: {
    items: Array<{
      clientReference: string;
      media: RegisteredMedia;
    }>;
  };
};

export type Specialty = {
  id: string;
  slug: string;
  name: string;
  category: string;
  description: string | null;
  display_order: number;
  icon?: string;
  enum_slug?: string | null;
  availability?: {
    isAvailable: boolean;
    expiresAt: string | null;
    activeFormUid: string | null;
  };
};

export type IntakeFormApiRecord = {
  id?: string;
  formUid: string;
  referenceUid?: string;
  patientEmail?: string;
  patientId?: string;
  specialty?: string;
  status?: string;
  submittedAt?: string | null;
  verifiedAt?: string | null;
  identity?: Record<string, unknown>;
  responses?: Array<{ questionId: string; answer: unknown; question?: unknown }>;
  media?: Array<Record<string, unknown>>;
  portalContext?: string;
  verificationAttempts?: number;
  verificationMethod?: string;
  createdAt?: string;
  updatedAt?: string;
};

type BatchMediaRegistrationRequestItem = {
  clientReference: string;
  mediaType: "photo";
  s3BucketName: string;
  s3StoragePath: string;
  originalFilename: string;
  mimeType: string;
  fileSize: number;
  photoPosition?: string;
  photoSequence?: number;
  replaceMediaId?: string;
  metadata?: Record<string, unknown>;
};

type VerificationResponse = {
  data: {
    success: boolean;
    accessToken?: string;
    refreshToken?: string;
    expiresAt?: number;
    user: {
      id: string;
      email: string;
      role: string;
      onboardingComplete: boolean;
    };
    intakeForm?: Record<string, unknown>;
    serviceRequest?: Record<string, unknown>;
  };
};

/** Channel the continuation OTP is delivered over. */
export type ContinuationChannel = "email" | "sms";

export type ContinuationStartResponse = {
  data: {
    challengeId?: string;
    expiresAt?: string;
    /** Masked destination (e.g. "j•••@mail.com") when the API chooses to expose one. */
    maskedDestination?: string;
  };
};

export type ContinuationVerifyData = {
  accessToken: string;
  refreshToken?: string;
  draftToken?: string;
  form: Record<string, unknown>;
};

type StartTrustedSessionPayload = {
  formUid: string;
  token: string;
  source?: string;
};

type StartTrustedSessionSuccessData = {
  success: boolean;
  redirectUrl: string;
  accessToken?: string;
  refreshToken?: string;
  expiresAt?: number;
  refreshExpiresAt?: number;
  user?: {
    id: string;
    email: string;
    role: string;
    onboardingComplete: boolean;
  };
  intakeForm?: Record<string, unknown>;
};

type StartTrustedSessionResponse = {
  data: StartTrustedSessionSuccessData;
  message?: string;
};

const MEDICAL_QUESTION_ORDER = [
  "dateOfBirth",
  "height",
  "weight",
  "currentMedicalConditions",
  "preExistingConditions",
  "otherCondition",
  "pastSurgeries",
  "currentMedications",
  "allergies",
  "allergiesMedications",
  "smokingHabits",
  "alcoholUse",
  "alcoholConsumption",
  "exerciseFrequency",

  //NEED TO REMOVE
  // "travelConcerns",
  // "budgetExpectations",
  // "personalInterest",
  // "travelAlone",

  //DENTAL ONLY
  //DENTAL ONLY - MOVED TO DENTISTRY_CORE_KEYS


] as const satisfies readonly (keyof IntakeData)[];

// Optional experience keys for tours and services
const OPTIONAL_EXPERIENCE_KEYS = [
  "selectedServices",
  "selectedTours",
  "selectedExperience",
  "additionalServicesCost",
] as const satisfies readonly (keyof IntakeData)[];


// Optional experience keys for tours and services
const DENTISTRY_DYNAMIC_QUESTION_KEYS = [
  "orthodonticHistory",
  "orthodonticGoals",
  "treatmentDuration",
  "appliancePreference",
  "missingTeethCount",
  "missingTeethDuration",
  "boneGraftHistory",
  "implantExpectations",
  "cosmeticGoals",
  "previousCosmetic",
  "smileConfidence",
  "socialImpact",
  "reconstructionReason",
  "currentPainLevel",
  "functionIssues",
  "treatmentPriority",
  "veneerGoals",
  "teethToTreat",
  "currentTeethCondition",
  "previousCosmetic",
  "crownReason",
  "numberOfCrowns",
  "crownMaterial",
  "urgencyLevel",
  "whiteningGoal",
  "stainingType",
  "previousWhitening",
  "sensitivityConcern",
  "lastDentalVisit",
  "dentalConcerns",
  "treatmentInterest",
];

const DENTISTRY_CORE_KEYS = [
  "dentalProcedureType",
  "dentalDiscomfort",
] as const;

export const VASCULAR_KEYS = [
  "vascularPrimaryGoal",
  "vascularSymptoms",
  "vascularSymptomTypes",
  "vascularDiagnosisStatus",
  "vascularUrgency",
  "vascularProceduralExpectation",
] as const;

export const HAIR_TRANSPLANT_KEYS = [
  "hairTransplantTreatmentAreas",
  "hairTransplantTreatmentAreasOther",
  "hairTransplantProgression",
  "hairTransplantRelevantFactors",
  "hairTransplantPreviousTreatments",
  "hairTransplantOutcomeExpectation",
  "hairTransplantTimeline",
] as const;

export const OPTICAL_KEYS = [
  "opticalPrimaryReason",
  "opticalVisionCorrection",
  "opticalLaterality",
  "opticalSymptoms",
  "opticalProcedureInterest",
] as const;

export const PLASTIC_SURGERY_KEYS = [
  "plasticAreaOfInterest",
  "plasticProcedureType",
  "plasticSpecificConcern",
  "plasticPreviousSurgery",
  "plasticOutcomePriority",
] as const;

export const DIAGNOSTICS_KEYS = [
  "diagnosticsPurpose",
  "diagnosticsType",
  "diagnosticsArea",
  "diagnosticsTimeSensitivity",
  "diagnosticsCategory",
  "diagnosticsReason",
  "diagnosticsReasonOther",
  "diagnosticsHasSymptoms",
  "diagnosticsSymptoms",
  "diagnosticsSymptomOther",
  "diagnosticsConditions",
  "diagnosticsConditionOther",
  "diagnosticsDoctorStatus",
  "diagnosticsDoctorList",
  "diagnosticsScope",
  "diagnosticsRecentTesting",
  "diagnosticsShareRecentResults",
  "diagnosticsLabUrgency",
  "diagnosticsFasting",
  "diagnosticsResultsUsage",
  "diagnosticsFollowUp",
] as const;

export const FERTILITY_KEYS = [
  "fertilityCareFocus",
  "fertilityPatientType",
  "fertilityPreviousAttempts",
  "fertilityKnownDiagnosis",
  "fertilityTimeline",
] as const;

export const STEM_CELL_KEYS = [
  "stemCellReason",
  "stemCellLocation",
  "stemCellDiagnosisStatus",
  "stemCellTreatmentGoal",
  "stemCellPriorTreatments",
] as const;

export const ORTHOPEDIC_KEYS = [
  "orthopedicPrimaryConcern",
  "orthopedicAffectedArea",
  "orthopedicDuration",
  "orthopedicPriorTreatments",
] as const;

export const ENT_KEYS = [
  "entPrimaryConcern",
  "entSymptoms",
  "entDuration",
  "entPreviousSurgeries",
] as const;

export const NEURO_SURGERY_KEYS = [
  "neuroSurgeryPrimaryConcern",
  "neuroSurgerySymptoms",
  "neuroSurgeryDiagnosisStatus",
  "neuroSurgeryPriorTreatment",
  "neuroSurgeryTimeline",
] as const;

export const UROLOGY_KEYS = [
  "urologyPrimaryConcern",
  "urologySymptoms",
  "urologyDuration",
  "urologyDiagnosisStatus",
  "urologyPriorTreatment",
  "urologyTimeline",
] as const;

export const BARIATRIC_KEYS = [
  "bariatricPrimaryGoal",
  "bariatricPriorWeightLoss",
  "bariatricProcedureInterest",
  "bariatricPreviousAttempts",
  "bariatricCurrentDiet",
  "bariatricTimeline",
] as const;

export const WELLNESS_KEYS = [
  // General wellness
  "wellnessPrimaryGoal",
  "wellnessCurrentPractices",
  "wellnessSpecificConcerns",
  "wellnessTimeline",
  "wellnessPreviousExperience",
  "wellnessAdditionalNotes",

  // Acupuncture / Colema therapy
  "wellnessAcupunctureConcern",
  "wellnessAcupunctureTargetArea",
  "wellnessAcupunctureFrequency",

  // Hyperbaric
  "wellnessHyperbaricReason",
  "wellnessHyperbaricUrgency",
  "wellnessHyperbaricSessions",

  // Massage
  "wellnessMassageFocus",
  "wellnessMassagePressure",

  // Reflexology
  "wellnessReflexologyTarget",
  "wellnessReflexologyMainSymptoms",

  // Ozone
  "wellnessOzoneReason",
  "wellnessOzonePreviousExperience",

  // Functional doctor
  "wellnessFunctionalDoctorProgram",
  "wellnessFunctionalDoctorExperience",
] as const;

export const DETAILED_PROCEDURE_KEYS = [
  ...VASCULAR_KEYS,
  ...HAIR_TRANSPLANT_KEYS,
  ...OPTICAL_KEYS,
  ...PLASTIC_SURGERY_KEYS,
  ...DIAGNOSTICS_KEYS,
  ...FERTILITY_KEYS,
  ...STEM_CELL_KEYS,
  ...ORTHOPEDIC_KEYS,
  ...ENT_KEYS,
  ...NEURO_SURGERY_KEYS,
  ...UROLOGY_KEYS,
  ...BARIATRIC_KEYS,
  ...WELLNESS_KEYS,
] as const satisfies readonly (keyof IntakeData)[];

/**
 * Every `questionId` `buildFormPayload` can emit into `responses`.
 * Exported so the inverse mapper (`hydrateIntakeData`) can drop unknown ids
 * without keeping a second, drift-prone copy of this list.
 */
export const RESPONSE_QUESTION_KEYS: readonly string[] = [
  ...MEDICAL_QUESTION_ORDER,
  ...OPTIONAL_EXPERIENCE_KEYS,
  ...DENTISTRY_DYNAMIC_QUESTION_KEYS,
  ...DENTISTRY_CORE_KEYS,
  ...DETAILED_PROCEDURE_KEYS,
];

const isValidHttpDocumentUrl = (urlString: string): boolean => {
  try {
    const u = new URL(urlString);
    return (
      (u.protocol === "http:" || u.protocol === "https:") && Boolean(u.hostname)
    );
  } catch {
    return false;
  }
};

const normalizeAnswer = (
  value: unknown,
):
  | string
  | number
  | boolean
  | string[]
  | DocumentReferenceLink[]
  | Record<string, unknown>
  | null => {
  if (value === undefined || value === null) {
    return null;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return value;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return null;
    }
    const allDocLinkObjects = value.every(
      (item) =>
        item !== null &&
        typeof item === "object" &&
        !Array.isArray(item) &&
        typeof (item as Record<string, unknown>).url === "string",
    );
    if (allDocLinkObjects) {
      const mapped: DocumentReferenceLink[] = [];
      for (const item of value as Array<{ url: string; label?: unknown }>) {
        const url = item.url.trim();
        if (!isValidHttpDocumentUrl(url)) {
          continue;
        }
        const entry: DocumentReferenceLink = { url };
        if (typeof item.label === "string") {
          const lab = item.label.trim();
          if (lab.length > 0) {
            entry.label = lab;
          }
        }
        mapped.push(entry);
      }
      return mapped.length > 0 ? mapped : null;
    }
    return value.length > 0 ? (value as string[]) : null;
  }

  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>);
    return entries.length > 0 ? (value as Record<string, unknown>) : null;
  }

  return null;
};

// Humanize camelCase/underscore keys for easier downstream reading
const formatQuestionLabel = (questionId: string): string => {
  const withSpaces = questionId
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2");

  return withSpaces
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

type IntakeQuestionPayload = {
  id: string;
  prompt: string;
  type?: string;
  category?: string;
  options?: string[];
  metadata?: Record<string, unknown>;
};

import { OPTION_CODE_TO_LABEL } from './intakeOptionMaps';

export type BuildFormPayloadOptions = {
  /** When false, omit specialty (e.g. contact-info draft before procedure is confirmed). */
  includeSpecialty?: boolean;
};

//PAYLOAD BUILD
export const buildFormPayload = (
  data: IntakeData,
  options?: BuildFormPayloadOptions,
) => {
  // Clean and validate identity fields - ensure required fields are not empty
  const cleanString = (value: string | undefined): string | undefined => {
    if (!value || typeof value !== 'string') return undefined;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  };

  // Build identity object
  // Required fields are always included (even if empty) so backend can validate
  // Optional fields are only included if they have values
  const identity: Record<string, string> = {
    firstName: cleanString(data.firstName) || '',
    lastName: cleanString(data.lastName) || '',
    email: cleanString(data.email) || '',
  };
  
  const dateOfBirth = cleanString(data.dateOfBirth);
  if (dateOfBirth) identity.dateOfBirth = dateOfBirth;
  
  const city = cleanString(data.city);
  if (city) identity.city = city;
  
  const state = cleanString(data.state);
  if (state) identity.state = state;
  
  const country = cleanString(data.country);
  if (country) identity.country = country;

  const phoneCountryIso =
    cleanString(data.phoneCountry) ?? country;
  if (phoneCountryIso) {
    const dialCode = getDialCodeForCountry(phoneCountryIso);
    if (dialCode) identity.countryCode = dialCode;
  }

  const phoneRaw = cleanString(data.phone);
  if (phoneRaw) {
    const repaired = phoneCountryIso
      ? repairIntakePhoneFields(phoneRaw, phoneCountryIso)
      : { phone: phoneRaw, country: phoneCountryIso ?? "" };
    if (repaired.phone) identity.phone = repaired.phone;
  }
  
  if (data.preferredLanguage) {
    identity.preferredLanguage = data.preferredLanguage;
  }

  const responses: Array<{
    questionId: string;
    answer:
      | string
      | number
      | boolean
      | string[]
      | Record<string, unknown>
      | Record<string, unknown>[]
      | unknown
      | null;
    question?: IntakeQuestionPayload;
  }> = [];

  const pushResponse = (
    questionId: keyof IntakeData,
    includeWhenNull = true,
  ) => {
    let answer = normalizeAnswer((data as IntakeData)[questionId]);
    
    // --- TRANSFORM TO HUMAN READABLE LABEL ---
    if (answer !== null) {
      const mapping = OPTION_CODE_TO_LABEL[questionId];
      if (mapping) {
        if (Array.isArray(answer)) {
          if (answer.every((item) => typeof item === "string")) {
            answer = (answer as string[]).map(
              (item: string) => mapping[item] || item,
            );
          }
        } else if (typeof answer === "string") {
          // Map single string value
          answer = mapping[answer] || answer;
        }
      }
    }
    // -----------------------------------------

    if (answer === null && !includeWhenNull) {
      return;
    }
    responses.push({
      questionId,
      question: {
        id: String(questionId),
        prompt: formatQuestionLabel(questionId),
      },
      answer,
    });
  };

  // Push medical questions in order, but skip unanswered fields.
  // V2 no longer asks many legacy questions, so sending null placeholders
  // creates noise and confusion for backend processing.
  MEDICAL_QUESTION_ORDER.forEach((questionId) => {
    pushResponse(questionId as keyof IntakeData, false);
  });

  // Push optional experience keys
  OPTIONAL_EXPERIENCE_KEYS.forEach((questionId) => {
    pushResponse(questionId as keyof IntakeData, false);
  });

  // Send each document reference link as its own response entry:
  // documentReferenceLinks_1, documentReferenceLinks_2, ...
  const documentLinks = Array.isArray(data.documentReferenceLinks)
    ? data.documentReferenceLinks
    : [];
  let documentLinkIndex = 1;
  documentLinks.forEach((link) => {
    const url = typeof link?.url === "string" ? link.url.trim() : "";
    if (!isValidHttpDocumentUrl(url)) {
      return;
    }
    const questionId = `documentReferenceLinks_${documentLinkIndex}`;
    responses.push({
      questionId,
      question: {
        id: questionId,
        prompt: `Document Reference Link ${documentLinkIndex}`,
      },
      answer: url,
    });
    documentLinkIndex += 1;
  });
  
  DENTISTRY_DYNAMIC_QUESTION_KEYS.forEach((questionId) => {
    pushResponse(questionId as keyof IntakeData, false);
  });
  
  // Determine relevant detailed questions based on procedure
  const getProcedureSlug = (p: IntakeData["procedures"]) => {
    if (typeof p === "string") return p;
    return p?.slug || "";
  };
  const procedureSlug = getProcedureSlug(data.procedures)
    .trim()
    .toLowerCase()
    .replace(/_/g, "-");
  
  // Add core dental questions only if procedure is dentistry
  if (!procedureSlug || procedureSlug === "dentistry" || procedureSlug === "general-dentistry") {
    DENTISTRY_CORE_KEYS.forEach((questionId) => {
      pushResponse(questionId as keyof IntakeData, false);
    });
  }
  
  let detailedKeys: readonly string[] = [];
  
  if (procedureSlug === "vascular") {
    detailedKeys = VASCULAR_KEYS;
  } else if (procedureSlug.includes("hair") || procedureSlug === "hair-transplant") {
    detailedKeys = HAIR_TRANSPLANT_KEYS;
  } else if (procedureSlug.includes("optical") || procedureSlug === "optics") {
    detailedKeys = OPTICAL_KEYS;
  } else if (procedureSlug.includes("plastic") || procedureSlug === "cosmetic") {
    detailedKeys = PLASTIC_SURGERY_KEYS;
  } else if (procedureSlug.includes("diagnostic") || procedureSlug.includes("imaging") || procedureSlug === "radiology") {
    detailedKeys = DIAGNOSTICS_KEYS;
  } else if (procedureSlug === "fertility") {
    detailedKeys = FERTILITY_KEYS;
  } else if (procedureSlug === "orthopedic" || procedureSlug === "orthopedics") {
    detailedKeys = ORTHOPEDIC_KEYS;
  } else if (
    procedureSlug === "ent" ||
    procedureSlug === "ear-nose-throat" ||
    procedureSlug === "ear-nose-and-throat"
  ) {
    detailedKeys = ENT_KEYS;
  } else if (procedureSlug === "neuro-surgery") {
    detailedKeys = NEURO_SURGERY_KEYS;
  } else if (procedureSlug === "urology") {
    detailedKeys = UROLOGY_KEYS;
  } else if (procedureSlug === "bariatric" || procedureSlug.includes("bariatric")) {
    detailedKeys = BARIATRIC_KEYS;
  } else if (
    procedureSlug.includes("wellness") ||
    procedureSlug.includes("acupuncture") ||
    procedureSlug.includes("colema") ||
    procedureSlug.includes("hyperbaric") ||
    procedureSlug.includes("massage") ||
    procedureSlug.includes("reflexology") ||
    procedureSlug.includes("ozone") ||
    procedureSlug.includes("functional") ||
    procedureSlug.includes("quantum") ||
    procedureSlug.includes("iv") ||
    procedureSlug.includes("yoga") ||
    procedureSlug.includes("chinese")
  ) {
    detailedKeys = WELLNESS_KEYS;
  } else if (procedureSlug.includes("stem")) {
    detailedKeys = STEM_CELL_KEYS;
  }

  // Push detailed procedure questions
  // Only send keys for the CURRENT procedure
  detailedKeys.forEach((questionId) => {
    pushResponse(questionId as keyof IntakeData, false);
  });

  // Determine specialty from selected procedure using slug-to-enum mapping.
  // If no procedure is selected yet (e.g. contact-info draft creation),
  // omit specialty from payload instead of sending a default.
  // The procedures field can be:
  // 1. A ProcedureType object (from database) with slug property
  // 2. A string slug (legacy support)
  // 3. Empty/null (specialty omitted)
  const procedures = data.procedures;
  let specialty: string | undefined;
  
  if (procedures) { 
    if (typeof procedures === 'string') {
      // Handle string slug
      specialty = getSpecialtyTag(procedures);
    } else if (typeof procedures === 'object') {
      // Handle ProcedureType object from database
      if (procedures.slug) {
        specialty = getSpecialtyTag(procedures);
      } else if (procedures.category) {
        // Legacy fallback for category-based mapping
        specialty = getSpecialtyTag(procedures.category);
      }
    }
  }

  return {
    identity,
    ...(options?.includeSpecialty !== false && specialty ? { specialty } : {}),
    responses,
    portalContext: "patient" as const,
  };
};

export const patientIntakeApi = {
  
  async submitIntake(data: IntakeData, options?: BuildFormPayloadOptions) {
    const payload = buildFormPayload(data, options);
    const requestBody = { formData: payload };
    
    // Log payload for debugging validation errors
    console.log(
      "Submitting intake request body:",
      JSON.stringify(requestBody, null, 2),
    );
    
    const startResponse = await fetch(
      getVersionedApiPath("/patient-intake/forms"),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      },
    );
    const startResult = (await handleApiResponse(
      startResponse,
    )) as StartIntakeApiResponse;
    // Form creation stays anonymous, but the response carries the draft token
    // every later form/media call must present.
    captureDraftToken(startResult);
    return { data: startResult.data };
  },

  async startTrustedSession(
    payload: StartTrustedSessionPayload,
  ): Promise<StartTrustedSessionResponse> {
    console.log(
      "[TrustedSession] Calling start-session with payload:",
      JSON.stringify(payload, null, 2),
    );

    const response = await fetch(
      getVersionedApiPath("/patient-intake/trusted/start-session"),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
    );

    const parsed = (await handleApiResponse(
      response,
    )) as StartTrustedSessionResponse;

    console.log(
      "[TrustedSession] start-session response:",
      JSON.stringify(parsed, null, 2),
    );

    return parsed;
  },

  async getCurrentDisclosure(
    channel: "sms" | "voice",
    locale: "en" | "es",
    fetchImpl: typeof fetch = fetch,
  ) {
    const response = await fetchImpl(
      getVersionedApiPath(
        `/consents/disclosures/current?channel=${channel}&locale=${locale}`,
      ),
    );
    return parseDisclosure(await handleApiResponse(response));
  },

  async finalizeIntake(
    formUid: string,
    consents?: { channel: "sms" | "voice"; disclosureId: string }[],
    fetchImpl: typeof fetch = fetch,
  ) {
    const finalizeResponse = await fetchImpl(
      getVersionedApiPath(`/patient-intake/forms/${formUid}/finalize`),
      {
        method: "POST",
        headers: withDraftAuth({ "Content-Type": "application/json" }),
        body: JSON.stringify(buildFinalizeBody(consents)),
      },
    );
    return handleApiResponse(
      finalizeResponse,
    ) as Promise<FinalizeIntakeApiResponse>;
  },

  async getPhotoUploadUrl(
    formUid: string,
    mimeType: string,
    size: number,
    originalFilename: string,
  ): Promise<MediaUploadResponse> {
    const response = await fetch(
      getVersionedApiPath(`/patient-intake/forms/${formUid}/media/presign`),
      {
        method: "POST",
        headers: withDraftAuth({ "Content-Type": "application/json" }),
        body: JSON.stringify({ mimeType, size, originalFilename }),
      },
    );

    return handleApiResponse(response);
  },

  async uploadPhotoToCloud(uploadUrl: string, file: File) {
    const response = await fetch(uploadUrl, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": file.type },
    });

    if (!response.ok) {
      throw new Error("Failed to upload photo to cloud storage");
    }
  },

  async registerPhoto(
    formUid: string,
    bucketName: string,
    storagePath: string,
    originalFilename: string,
    mimeType: string,
    fileSize: number,
    photoPosition?: string,
    photoSequence?: number,
    replaceMediaId?: string,
  ): Promise<MediaRegistrationResponse> {
    const response = await fetch(
      getVersionedApiPath(`/patient-intake/forms/${formUid}/media`),
      {
        method: "POST",
        headers: withDraftAuth({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          mediaType: "photo",
          s3BucketName: bucketName,
          s3StoragePath: storagePath,
          originalFilename,
          mimeType,
          fileSize,
          photoPosition,
          photoSequence,
          replaceMediaId,
        }),
      },
    );

    return handleApiResponse(response);
  },

  async getBatchPhotoUploadUrls(
    formUid: string,
    files: BatchPresignUploadRequest[],
  ): Promise<BatchPresignUploadResponse> {
    const response = await fetch(
      getVersionedApiPath(`/patient-intake/forms/${formUid}/media/batch/presign`),
      {
        method: "POST",
        headers: withDraftAuth({ "Content-Type": "application/json" }),
        body: JSON.stringify({ files }),
      },
    );

    return handleApiResponse(response);
  },

  async registerMediaBatch(
    formUid: string,
    items: BatchMediaRegistrationRequestItem[],
  ): Promise<BatchMediaRegistrationResponse> {
    const response = await fetch(
      getVersionedApiPath(`/patient-intake/forms/${formUid}/media/batch`),
      {
        method: "POST",
        headers: withDraftAuth({ "Content-Type": "application/json" }),
        body: JSON.stringify({ items }),
      },
    );

    return handleApiResponse(response);
  },

  async uploadPhoto(
    formUid: string,
    file: File,
    options?: {
      photoPosition?: string;
      photoSequence?: number;
      replaceMediaId?: string;
    },
  ): Promise<MediaRegistrationResponse> {
    const presignResponse = await this.getPhotoUploadUrl(
      formUid,
      file.type,
      file.size,
      file.name,
    );
    await this.uploadPhotoToCloud(presignResponse.data.uploadUrl, file);
    return this.registerPhoto(
      formUid,
      presignResponse.data.bucketName,
      presignResponse.data.storagePath,
      file.name,
      file.type,
      file.size,
      options?.photoPosition,
      options?.photoSequence,
      options?.replaceMediaId,
    );
  },

  async sendVerificationCode(email: string, formUid: string) {
    const response = await fetch(
      getVersionedApiPath("/patient-intake/send-verification-code"),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, method: "otp_email", formUid }),
      },
    );

    return handleApiResponse(response) as Promise<{ data: OtpChallenge }>;
  },

  async verifyEmail(
    email: string,
    token: string,
    formUid: string,
    challengeId?: string,
  ) {
    const response = await fetch(
      getVersionedApiPath("/patient-intake/verify-email"),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, token, challengeId, formUid }),
      },
    );

    return handleApiResponse(response) as Promise<VerificationResponse>;
  },

  /**
   * Step 1 of the voice-assistant continuation: ask the Manager API to send a
   * one-time code for `formUid`. Deliberately returns nothing about the patient
   * or whether the form exists — the caller must not branch on it.
   */
  async startContinuation(
    formUid: string,
    options?: { channel?: ContinuationChannel },
  ): Promise<ContinuationStartResponse> {
    const response = await fetch(
      getVersionedApiPath("/patient-intake/continue/start"),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          formUid,
          channel: options?.channel ?? "email",
        }),
      },
    );

    return handleApiResponse(response) as Promise<ContinuationStartResponse>;
  },

  /**
   * Step 2: exchange the code for intake session credentials plus the form.
   * Captures the returned `draftToken` so every later form/media call carries it.
   */
  async verifyContinuation(
    formUid: string,
    code: string,
    challengeId?: string,
  ): Promise<ContinuationVerifyData> {
    const response = await fetch(
      getVersionedApiPath("/patient-intake/continue/verify"),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ formUid, code, challengeId }),
      },
    );

    const parsed = (await handleApiResponse(response)) as
      | { data?: ContinuationVerifyData }
      | ContinuationVerifyData;
    const data = ((parsed as { data?: ContinuationVerifyData }).data ??
      parsed) as ContinuationVerifyData;
    captureDraftToken(data);
    return data;
  },

  async startAuth(payload: {
    email: string;
    formUid: string;
    referenceUid: string;
    forceResend?: boolean;
  }) {
    const response = await fetch(
      getVersionedApiPath("/patient-intake/auth/start"),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
    );

    return handleApiResponse(response) as Promise<{ data: MagicLinkChallenge }>;
  },

  async completeMagicLinkCallback(body: {
    accessToken: string;
    refreshToken?: string;
    formUid: string;
    referenceUid: string;
  }) {
    const response = await fetch(
      getVersionedApiPath("/patient-intake/auth/magic-link/callback"),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      },
    );

    return handleApiResponse(response);
  },

  async getIntakeStatus(formUid: string) {
    const response = await fetch(
      `${getVersionedApiPath(`/patient-intake/forms/${formUid}/status`)}?includeMedia=true`,
      { headers: withDraftAuth() },
    );

    return handleApiResponse(response);
  },

  async getIntakeFormByEmail(email: string) {
    // Validate email before making request
    if (!email || typeof email !== 'string' || email.trim().length === 0) {
      throw new Error("Email is required");
    }

    const trimmedEmail = email.trim().toLowerCase();
    const encodedEmail = encodeURIComponent(trimmedEmail);
    
    const response = await fetch(
      getVersionedApiPath(`/patient-intake/forms/by-email/${encodedEmail}`),
    );

    // Handle 404 specifically (form not found)
    if (response.status === 404) {
      throw new Error(`No intake form found for email: ${trimmedEmail}`);
    }

    // Handle 422 specifically (validation error)
    if (response.status === 422) {
      try {
        const errorData = await response.json();
        const errorMessage = errorData?.error?.message || errorData?.message || "Invalid email or intake form not found";
        throw new Error(errorMessage);
      } catch {
        throw new Error(`Invalid email format or intake form not found for: ${trimmedEmail}`);
      }
    }

    return handleApiResponse(response);
  },

  async updateIntake(formUid: string, data: IntakeData) {
    const payload = buildFormPayload(data);
    const requestBody = { formData: payload };
    console.log(
      "Updating intake request body:",
      JSON.stringify(requestBody, null, 2),
    );
    const response = await fetch(
      getVersionedApiPath(`/patient-intake/forms/${formUid}`),
      {
        method: "PATCH",
        headers: withDraftAuth({ "Content-Type": "application/json" }),
        body: JSON.stringify(requestBody),
      },
    );
    return handleApiResponse(response);
  },

  async getFormMedia(formUid: string): Promise<IntakeMedia[]> {
    const response = await fetch(
      getVersionedApiPath(`/patient-intake/forms/${formUid}/media`),
      { headers: withDraftAuth() },
    );

    const payload = (await handleApiResponse(response)) as unknown;

    if (Array.isArray(payload)) {
      return payload;
    }

    if (payload && typeof payload === "object") {
      const { data } = payload as { data?: unknown };
      if (Array.isArray(data)) {
        return data;
      }
    }

    return [];
  },

  async deleteForm(formUid: string) {
    const response = await fetch(
      getVersionedApiPath(`/patient-intake/forms/${formUid}`),
      {
        method: "DELETE",
      },
    );

    return handleApiResponse(response);
  },

  async deleteMedia(mediaId: string): Promise<unknown> {
    const response = await fetch(
      getVersionedApiPath(`/patient-intake/media/${mediaId}`),
      {
        method: "DELETE",
        headers: withDraftAuth(),
      },
    );

    return handleApiResponse(response);
  },

  /**
   * Patient-intake specialties. v1 supports `language=es` (localized `name` from `name_es`).
   * v2 currently documents only `email` and `isDev`; `language` is omitted on v2 until the API accepts it.
   */
  async getSpecialties(
    email?: string,
    isDev: boolean = false,
    requestedLanguage: "en" | "es" = "en",
  ): Promise<{ data: Specialty[], devMode?: boolean }> {
    const queryParams = new URLSearchParams();
    if (email) {
      queryParams.append('email', email);
    }
    if (isDev) {
      queryParams.append('isDev', 'true');
    }else{
      queryParams.append('isDev', 'false');
    }
    const apiVersion = getApiVersion().toLowerCase();
    if (apiVersion === "v1" && requestedLanguage === "es") {
      queryParams.append("language", "es");
    }
    const queryString = queryParams.toString();
    const url = `${getVersionedApiPath("/patient-intake/specialties")}${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(
      url,  
      {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      },
    );

    return handleApiResponse(response);
  },

  async getHealth() {
    const response = await fetch(getVersionedApiPath("/patient-intake/health"));
    return handleApiResponse(response);
  },

  async getFormByUid(formUid: string) {
    const response = await fetch(
      getVersionedApiPath(`/patient-intake/forms/${formUid}`),
      { headers: withDraftAuth() },
    );
    return handleApiResponse(response) as Promise<{
      data: IntakeFormApiRecord;
      message?: string;
    }>;
  },

  async getMediaDownloadUrl(mediaId: string) {
    const response = await fetch(
      getVersionedApiPath(`/patient-intake/media/${mediaId}/url`),
      { headers: withDraftAuth() },
    );
    return handleApiResponse(response);
  },

  async listServiceRequests(filters?: { includeInactive?: boolean; patientId?: string }) {
    const queryParams = new URLSearchParams();
    if (typeof filters?.includeInactive === "boolean") {
      queryParams.append("includeInactive", String(filters.includeInactive));
    }
    if (filters?.patientId) {
      queryParams.append("patientId", filters.patientId);
    }

    const query = queryParams.toString();
    const response = await fetch(
      `${getVersionedApiPath("/patient-intake/service-requests")}${query ? `?${query}` : ""}`,
    );
    return handleApiResponse(response);
  },

  async createSubmission(payload: V2IntakeSubmissionPayload) {
    const response = await fetch(getVersionedApiPath("/intake/submissions"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    return handleApiResponse(response);
  },

  async getSubmission(submissionId: string) {
    const response = await fetch(getVersionedApiPath(`/intake/submissions/${submissionId}`));
    return handleApiResponse(response);
  },

  async reviewSubmission(submissionId: string, payload: V2IntakeReviewPayload) {
    const response = await fetch(getVersionedApiPath(`/intake/submissions/${submissionId}/review`), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    return handleApiResponse(response);
  },
};

/**
 * Media IDs to remove from the server when the user changes specialty (Documents & uploads,
 * dental photos, additional dental photos). Excludes local `File` blobs not yet registered.
 */
export function collectServerMediaIdsForProcedureReset(
  data: IntakeData,
): string[] {
  const ids = new Set<string>();

  const addUploadList = (
    list?: NonNullable<IntakeData["uploads"]>[keyof IntakeData["uploads"]],
  ) => {
    for (const item of list ?? []) {
      if (
        item &&
        typeof item === "object" &&
        (typeof File === "undefined" || !(item instanceof File)) &&
        "id" in item &&
        typeof (item as { id: unknown }).id === "string"
      ) {
        const id = (item as { id: string }).id.trim();
        if (id.length > 0) ids.add(id);
      }
    }
  };

  addUploadList(data.uploads?.idPassport);
  addUploadList(data.uploads?.labsImaging);
  addUploadList(data.uploads?.photos);

  Object.values(data.photoMediaMap ?? {}).forEach((id) => {
    if (typeof id === "string" && id.trim().length > 0) ids.add(id.trim());
  });
  (data.additionalPhotoMediaIds ?? []).forEach((id) => {
    if (typeof id === "string" && id.trim().length > 0) ids.add(id.trim());
  });

  return [...ids];
}

const DENTAL_MEDIA_DELETE_LOG = "[intake:dental-media-delete]";

/**
 * Parallel DELETE for media IDs with structured console logging (e.g. leaving dentistry specialty).
 */
export async function deleteMediaIdsWithLogging(
  mediaIds: string[],
  logContext: Record<string, unknown>,
): Promise<void> {
  if (mediaIds.length === 0) return;

  console.info(`${DENTAL_MEDIA_DELETE_LOG} batch START`, {
    ...logContext,
    count: mediaIds.length,
    mediaIds: [...mediaIds],
  });

  const settled = await Promise.allSettled(
    mediaIds.map(async (mediaId) => {
      const startedAt = performance.now();
      console.info(`${DENTAL_MEDIA_DELETE_LOG} item START`, {
        ...logContext,
        mediaId,
      });

      try {
        const responseBody = await patientIntakeApi.deleteMedia(mediaId);
        const durationMs = Math.round(performance.now() - startedAt);
        let responseJson: string | undefined;
        try {
          responseJson =
            responseBody !== undefined && responseBody !== null
              ? JSON.stringify(responseBody, null, 2)
              : undefined;
        } catch {
          responseJson = String(responseBody);
        }
        console.info(`${DENTAL_MEDIA_DELETE_LOG} item SUCCESS`, {
          ...logContext,
          mediaId,
          durationMs,
          responseBody,
          responseJson,
        });
        return responseBody;
      } catch (error) {
        const durationMs = Math.round(performance.now() - startedAt);
        console.error(`${DENTAL_MEDIA_DELETE_LOG} item FAILED`, {
          ...logContext,
          mediaId,
          durationMs,
          error,
          errorMessage: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }
    }),
  );

  console.info(`${DENTAL_MEDIA_DELETE_LOG} batch COMPLETE`, {
    ...logContext,
    results: mediaIds.map((mediaId, i) => {
      const r = settled[i]!;
      if (r.status === "fulfilled") {
        return {
          mediaId,
          status: "fulfilled" as const,
          response: r.value,
        };
      }
      return {
        mediaId,
        status: "rejected" as const,
        reason:
          r.reason instanceof Error ? r.reason.message : String(r.reason),
      };
    }),
  });
}
